/**
 * Ingressos gerados do evento — todos os status — e a busca ao portador.
 *
 * A busca é local, sobre o que já foi baixado: acha por nome, CPF, e-mail,
 * pedido ou código. É o caminho de quem chegou na portaria sem o ingresso em
 * mãos, e por isso precisa funcionar sem sinal.
 *
 * Cada cartão já responde "eu não entrei ainda?" antes de o operador perguntar:
 * quem consta como validado mostra a hora em vez do botão de validar.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {ArrowLeft, Check, Clock, CloudOff, Search} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {
  listarIngressos,
  resumoDoEvento,
  statusDisponiveis,
  type IngressoListado,
} from '../dados/repositorio';
import {comMilhar, horaDe} from '../ui/datas';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Ingressos'>;

const PAGINA = 50;

/**
 * Nome do status como o operador fala. As letras são as do servidor
 * (`CValidaSync.getStatusNome` no TMobi): `A` aprovado, `C` cancelado ou
 * devolvido, `W` aguardando. Letra desconhecida aparece como veio.
 */
function nomeDoStatus(status: string): string {
  if (status === 'A') {
    return txt('Aprovado');
  }
  if (status === 'C') {
    return txt('Cancelado');
  }
  if (status === 'W') {
    return txt('Aguardando');
  }
  return status;
}

export function TelaIngressos({navigation, route}: Props) {
  const {eventoId, eventoNome} = route.params;
  const t = useTema();
  useIdioma();

  const [termo, setTermo] = useState('');
  const [termoAplicado, setTermoAplicado] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  /** Já entrou ou não. `null` traz os dois. */
  const [validado, setValidado] = useState<boolean | null>(null);
  const [statusPossiveis, setStatusPossiveis] = useState<string[]>([]);
  const [ingressos, setIngressos] = useState<IngressoListado[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [focado, setFocado] = useState(false);
  const [fim, setFim] = useState(false);

  useEffect(() => {
    statusDisponiveis(eventoId).then(setStatusPossiveis);
    resumoDoEvento(eventoId).then(r => setTotal(r.ingressos));
  }, [eventoId]);

  // Digitar não pode disparar consulta a cada tecla com dezenas de milhares de linhas.
  useEffect(() => {
    const relogio = setTimeout(() => setTermoAplicado(termo), 300);
    return () => clearTimeout(relogio);
  }, [termo]);

  const buscar = useCallback(
    async (offset: number) => {
      setCarregando(true);
      try {
        const pagina = await listarIngressos({
          eventoId,
          termo: termoAplicado,
          status: status ?? undefined,
          validado: validado ?? undefined,
          limite: PAGINA,
          offset,
        });
        setFim(pagina.length < PAGINA);
        setIngressos(atual => (offset === 0 ? pagina : [...atual, ...pagina]));
      } finally {
        setCarregando(false);
      }
    },
    [eventoId, termoAplicado, status, validado],
  );

  useEffect(() => {
    buscar(0);
  }, [buscar]);

  const filtros = useMemo(() => [null, ...statusPossiveis], [statusPossiveis]);

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]} edges={['top', 'bottom']}>
      <View style={[estilos.cabecalho, {borderBottomColor: t.superficie}]}>
        <Pressable
          onPress={() => navigation.goBack()}
          hitSlop={6}
          accessibilityLabel={txt('Voltar')}
          style={[estilos.botaoIcone, {borderColor: t.borda}]}>
          <ArrowLeft size={20} color={t.texto} strokeWidth={2.2} />
        </Pressable>

        <View style={{flex: 1}}>
          <Text style={[estilos.tituloCabecalho, {color: t.texto}]}>{txt('Buscar portador')}</Text>
          <Text
            style={[
              estilos.legendaCabecalho,
              {color: t.textoFraco, fontVariant: ['tabular-nums']},
            ]}
            numberOfLines={1}>
            {txt('{quantos} ingressos no aparelho', {quantos: comMilhar(total)})}
          </Text>
        </View>

        {/* A busca é sobre o que já está no aparelho, e funciona sem sinal.
            Só o ícone: é um estado permanente da tela, não um aviso — escrito
            por extenso, ocupava uma linha inteira para sempre dizer a mesma
            coisa. O texto segue no rótulo, para quem usa leitor de tela. */}
        <View
          accessible
          accessibilityLabel={txt('Busca local — funciona sem sinal')}
          style={{paddingHorizontal: 6}}>
          <CloudOff size={19} color={t.textoApagado} strokeWidth={2.2} />
        </View>
      </View>

      <View style={{paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12, gap: 12}}>
        <View
          style={{
            height: 56,
            borderRadius: 15,
            backgroundColor: t.superficie,
            borderWidth: focado ? 2 : 1,
            borderColor: focado ? t.primaria : t.borda,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 11,
            paddingHorizontal: 16,
          }}>
          <Search size={20} color={focado ? t.primaria : t.textoFraco} strokeWidth={2.4} />
          <TextInput
            style={{flex: 1, color: t.texto, fontSize: 18, fontFamily: fontes.semi, padding: 0}}
            value={termo}
            onChangeText={setTermo}
            onFocus={() => setFocado(true)}
            onBlur={() => setFocado(false)}
            placeholder={txt('Nome, CPF, e-mail, pedido ou código')}
            placeholderTextColor={t.textoApagado}
            autoCapitalize="none"
            autoCorrect={false}
            clearButtonMode="while-editing"
          />
        </View>

        {filtros.length > 1 ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{gap: 8}}>
            {filtros.map(f => {
              const ativo = status === f;
              return (
                <Pressable
                  key={f ?? 'todos'}
                  onPress={() => setStatus(f)}
                  style={{
                    paddingHorizontal: 14,
                    paddingVertical: 9,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: ativo ? t.texto : t.borda,
                    backgroundColor: ativo ? t.texto : 'transparent',
                  }}>
                  <Text
                    style={{
                      color: ativo ? t.fundo : t.textoFraco,
                      fontSize: 13,
                      fontFamily: fontes.negrito,
                    }}>
                    {f === null ? txt('Todos') : nomeDoStatus(f)}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        ) : null}

        {/* Entrada na portaria é outra pergunta que o status da venda: um
            ingresso aprovado pode já ter entrado. Por isso dois botões
            próprios, que ligam e desligam — nenhum aceso traz os dois. */}
        <View style={{flexDirection: 'row', gap: 8}}>
          {[
            {rotulo: txt('Já validados'), qual: true},
            {rotulo: txt('Não validados'), qual: false},
          ].map(({rotulo, qual}) => {
            const aceso = validado === qual;
            return (
              <Pressable
                key={rotulo}
                onPress={() => setValidado(aceso ? null : qual)}
                accessibilityState={{selected: aceso}}
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  height: 40,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: aceso ? t.primaria : t.borda,
                  backgroundColor: aceso ? t.sucessoFundo : 'transparent',
                }}>
                <Clock
                  size={15}
                  color={aceso ? t.destaque : t.textoFraco}
                  strokeWidth={2.2}
                />
                <Text
                  style={{
                    color: aceso ? t.destaque : t.textoFraco,
                    fontSize: 13,
                    fontFamily: aceso ? fontes.negrito : fontes.semi,
                  }}>
                  {rotulo}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <FlatList
        data={ingressos}
        keyExtractor={i => i.codbar}
        contentContainerStyle={{paddingHorizontal: 20, paddingBottom: 20, gap: 10}}
        keyboardShouldPersistTaps="handled"
        // Quantas telas de cartão ficam montadas fora da vista. O padrão são
        // 21 (dez para cada lado), o que num evento de dezenas de milhares vira
        // centenas de cartões vivos depois de rolar um pouco. Sete cobre a
        // rolagem normal do polegar e devolve o resto.
        windowSize={7}
        initialNumToRender={8}
        maxToRenderPerBatch={8}
        updateCellsBatchingPeriod={60}
        onEndReachedThreshold={0.4}
        onEndReached={() => {
          if (!carregando && !fim) {
            buscar(ingressos.length);
          }
        }}
        ListEmptyComponent={
          carregando ? (
            <ActivityIndicator color={t.primaria} />
          ) : (
            <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
              {termoAplicado
                ? txt('Nada encontrado com esse termo.')
                : txt('Nenhum ingresso baixado para este evento.')}
            </Text>
          )
        }
        ListFooterComponent={
          carregando && ingressos.length > 0 ? <ActivityIndicator color={t.primaria} /> : undefined
        }
        renderItem={({item}) => {
          const aprovado = item.status === 'A';
          const aguardando = item.status === 'W';
          const selo = aprovado
            ? {fundo: t.sucessoFundo, tinta: t.sucessoTinta}
            : aguardando
              ? {fundo: t.avisoFundo, tinta: t.avisoTinta}
              : {fundo: t.erroFundo, tinta: t.erroTinta};

          return (
            <View
              style={[
                estilos.cartao,
                {backgroundColor: t.superficie, borderColor: t.borda, gap: 8, padding: 14},
              ]}>
              <View style={{flexDirection: 'row', alignItems: 'flex-start', gap: 12}}>
                <View style={{flex: 1, gap: 2}}>
                  <Text
                    numberOfLines={2}
                    style={{
                      color: t.texto,
                      fontSize: 17,
                      lineHeight: 21,
                      fontFamily: fontes.extra,
                      letterSpacing: -0.4,
                    }}>
                    {item.nome || txt('Sem nome')}
                  </Text>
                  <Text
                    style={{
                      color: t.textoFraco,
                      fontSize: 14,
                      fontFamily: fontes.regular,
                      fontVariant: ['tabular-nums'],
                    }}>
                    {[item.cpf, item.pedidoId && txt('pedido {numero}', {numero: item.pedidoId})]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  <Text style={{color: t.textoFraco, fontSize: 14, fontFamily: fontes.regular}}>
                    {[item.setor, item.numero && txt('nº {numero}', {numero: item.numero})]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                </View>

                <View
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    backgroundColor: selo.fundo,
                  }}>
                  <Text
                    style={{
                      color: selo.tinta,
                      fontSize: 12,
                      fontFamily: fontes.extra,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}>
                    {nomeDoStatus(item.status)}
                  </Text>
                </View>
              </View>

              <Text style={[estilos.mono, {color: t.textoFraco, fontSize: 14}]}>{item.codbar}</Text>

              {item.validadoEm ? (
                /* Numa linha, não num bloco: quem já entrou é informação de
                   conferência, não ação. O bloco alto empurrava o próximo
                   resultado para fora da tela — e buscar portador é justamente
                   varrer vários parecidos até achar o certo. */
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    borderRadius: 10,
                    backgroundColor: t.infoFundo,
                    borderWidth: 1,
                    borderColor: t.infoBorda,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                  }}>
                  <Clock size={16} color={t.infoTinta} strokeWidth={2.4} />
                  <Text
                    numberOfLines={1}
                    style={{flex: 1, color: t.infoTinta, fontSize: 14, fontFamily: fontes.semi}}>
                    {txt('Já validado às {hora}', {hora: horaDe(item.validadoEm)}) +
                      ' · ' +
                      (item.validadoAqui ? txt('neste aparelho') : txt('em outro posto'))}
                  </Text>
                </View>
              ) : (
                <Pressable
                  onPress={() =>
                    navigation.navigate('Validador', {
                      eventoId,
                      eventoNome,
                      codbarInicial: item.codbar,
                    })
                  }
                  style={[estilos.botao, {height: 48, backgroundColor: t.primaria}]}>
                  <Check size={19} color={t.tintaPrimaria} strokeWidth={3} />
                  <Text style={[estilos.textoBotao, {color: t.tintaPrimaria, fontSize: 16}]}>
                    {txt('Validar este ingresso')}
                  </Text>
                </Pressable>
              )}
            </View>
          );
        }}
      />
    </SafeAreaView>
  );
}
